'use client';

import { createContext, type PropsWithChildren, type ReactNode, use, useMemo } from 'react';
import type { Interaction } from './util/interaction';

export interface AccessRequirement {
  write?: boolean;
  /** TODO future: scope token, e.g. 'payments/wire/command' */
  scope?: string;
}

export type AccessEvaluator = (requirement: AccessRequirement) => boolean;

const AccessContext = createContext<{ isAllowed: AccessEvaluator }>({ isAllowed: () => false });

interface AccessProviderProps {
  isAllowed: AccessEvaluator;
}

export function AccessProvider({ children, isAllowed }: PropsWithChildren<AccessProviderProps>) {
  const value = useMemo(() => ({ isAllowed }), [isAllowed]);

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}

export function useHasAccess(requirement: AccessRequirement): boolean {
  const { isAllowed } = use(AccessContext);

  return isAllowed(requirement);
}

export function useIsWriteAllowed(): boolean {
  return useHasAccess({ write: true });
}

export function useWriteGatedInteraction<T extends Interaction>(interaction: T | undefined): T | undefined {
  const { isAllowed } = use(AccessContext);

  if (!interaction) {
    return undefined;
  }

  if (interaction.requiresWrite && !isAllowed({ write: true })) {
    return undefined;
  }

  // if (interaction.requiresScope && !isAllowed({ scope: interaction.requiresScope })) {
  //   return undefined;
  // }

  return interaction;
}

type RequiresAccessProps = PropsWithChildren<AccessRequirement>;

/** Element-level gate for arbitrary renders: `<RequiresAccess write><CreateApiKeyDrawer trigger={…} /></RequiresAccess>` */
export function RequiresAccess({ children, scope, write }: RequiresAccessProps): ReactNode {
  const hasAccess = useHasAccess({ scope, write });

  return hasAccess ? children : null;
}
